import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../shared/Button';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import apiClient from '../../lib/api-client';

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';

export const ContactForm: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('contactForm.validation.nameRequired');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('contactForm.validation.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contactForm.validation.emailInvalid');
    }

    if (!formData.subject) {
      newErrors.subject = t('contactForm.validation.subjectRequired');
    }

    if (!formData.message.trim()) {
      newErrors.message = t('contactForm.validation.messageRequired');
    } else if (formData.message.trim().length < 20) {
      newErrors.message = t('contactForm.validation.messageMinLength');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setSubmitStatus('loading');

    try {
      await apiClient.post('/contact', formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="surface-card space-y-6 p-6 md:p-8">
      {/* Success Banner */}
      {submitStatus === 'success' && (
        <div className="flex items-center gap-2 rounded-2xl border border-success/25 bg-success/10 px-4 py-3 text-success">
          <CheckCircle className="h-5 w-5" />
          <span className="font-medium">{t('contactForm.successMessage')}</span>
        </div>
      )}

      {/* Error Banner */}
      {submitStatus === 'error' && (
        <div className="flex items-center gap-2 rounded-2xl border border-error/25 bg-error/10 px-4 py-3 text-error">
          <XCircle className="h-5 w-5" />
          <span className="font-medium">
            {t('contactForm.errorMessage')}
          </span>
        </div>
      )}

      {/* Name */}
      <div>
        <label htmlFor="name" className="mb-2 block text-sm font-medium text-slate-700">
          {t('contactForm.labels.name')}
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`w-full rounded-2xl border px-4 py-3 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 ${
            errors.name ? 'border-error bg-error/5' : 'border-primary/20 bg-white'
          }`}
          placeholder={t('contactForm.namePlaceholder')}
        />
        {errors.name && (
          <p className="mt-1 text-sm text-error">{errors.name}</p>
        )}
      </div>

      {/* Email */}
      <div>
        <label htmlFor="email" className="mb-2 block text-sm font-medium text-slate-700">
          {t('contactForm.labels.email')}
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={`w-full rounded-2xl border px-4 py-3 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 ${
            errors.email ? 'border-error bg-error/5' : 'border-primary/20 bg-white'
          }`}
          placeholder="you@email.com"
        />
        {errors.email && (
          <p className="mt-1 text-sm text-error">{errors.email}</p>
        )}
      </div>

      {/* Subject */}
      <div>
        <label htmlFor="subject" className="mb-2 block text-sm font-medium text-slate-700">
          {t('contactForm.labels.subject')}
        </label>
        <select
          id="subject"
          name="subject"
          value={formData.subject}
          onChange={handleChange}
          className={`w-full rounded-2xl border px-4 py-3 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 ${
            errors.subject ? 'border-error bg-error/5' : 'border-primary/20 bg-white'
          }`}
        >
          <option value="">{t('contactForm.selectSubject')}</option>
          <option value="technical">{t('contactForm.subjects.technical')}</option>
          <option value="account">{t('contactForm.subjects.account')}</option>
          <option value="payment">{t('contactForm.subjects.payment')}</option>
          <option value="feedback">{t('contactForm.subjects.feedback')}</option>
          <option value="partnership">{t('contactForm.subjects.partnership')}</option>
          <option value="other">{t('contactForm.subjects.other')}</option>
        </select>
        {errors.subject && (
          <p className="mt-1 text-sm text-error">{errors.subject}</p>
        )}
      </div>

      {/* Message */}
      <div>
        <label htmlFor="message" className="mb-2 block text-sm font-medium text-slate-700">
          {t('contactForm.labels.message')}
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={6}
          className={`w-full resize-y rounded-2xl border px-4 py-3 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 ${
            errors.message ? 'border-error bg-error/5' : 'border-primary/20 bg-white'
          }`}
          placeholder={t('contactForm.messagePlaceholder')}
        />
        {errors.message && (
          <p className="mt-1 text-sm text-error">{errors.message}</p>
        )}
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        variant="primary"
        className="w-full"
        disabled={submitStatus === 'loading'}
      >
        {submitStatus === 'loading' ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            {t('common.waiting')}
          </span>
        ) : (
          t('contactForm.submitButton')
        )}
      </Button>

      <p className="text-sm text-gray-500 text-center">
        {t('payments.form.agreeTerms')}{' '}
        <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{t('contactForm.privacyLink')}</a>.
      </p>
    </form>
  );
};

export default ContactForm;
